import {useCallback, useEffect, useMemo, useState} from "react";
import * as Sentry from "@sentry/react";
import {BackendApi} from "../api/api.ts";
import {User} from "../api/accessControl/User.ts";
import {Customer, CustomerDraft} from "../api/customers/Customer.ts";
import * as customersApi from "../api/customers/customersApi.ts";

function sortByName(customers: Customer[]): Customer[] {
    return [...customers].sort((a, b) => a.company_name.localeCompare(b.company_name, 'nl'));
}

export const useCustomers = (user: User) => {
    const [customers, setCustomers] = useState<Customer[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);

    const api = useMemo(() => new BackendApi(user.getToken()), [user]);

    const refresh = useCallback(async () => {
        try {
            const result = await customersApi.listCustomers(api);
            setCustomers(sortByName(result));
            setLoadError(null);
        } catch (error) {
            Sentry.captureException(error);
            setLoadError('Klanten konden niet geladen worden. Probeer het later opnieuw.');
        } finally {
            setIsLoading(false);
        }
    }, [api]);

    useEffect(() => {
        let cancelled = false;

        async function loadCustomers() {
            try {
                const result = await customersApi.listCustomers(api);
                if (cancelled) return;
                setCustomers(sortByName(result));
                setLoadError(null);
            } catch (error) {
                Sentry.captureException(error);
                if (!cancelled) setLoadError('Klanten konden niet geladen worden. Probeer het later opnieuw.');
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        }

        loadCustomers();
        return () => {
            cancelled = true;
        };
    }, [api]);

    const createCustomer = useCallback(async (draft: CustomerDraft): Promise<Customer> => {
        const customer = await customersApi.createCustomer(api, draft);
        await refresh();
        return customer;
    }, [api, refresh]);

    const changeCustomer = useCallback(async (id: string, draft: CustomerDraft): Promise<Customer> => {
        const customer = await customersApi.changeCustomer(api, id, draft);
        await refresh();
        return customer;
    }, [api, refresh]);

    const archiveCustomer = useCallback(async (id: string): Promise<void> => {
        await customersApi.archiveCustomer(api, id);
        await refresh();
    }, [api, refresh]);

    return {customers, isLoading, loadError, refresh, createCustomer, changeCustomer, archiveCustomer};
};
